// The activity log (design §12.2): every run, and what it did.
//
// A first-class view, not developer tooling. Its purpose is that someone who
// wants to know why the advisor said a thing can always find out.

import { useState } from "react";

import { api } from "../api/client";
import { when } from "../advisor/CommentCard";
import type { Run, RunStep } from "../advisor/types";
import { useDocument } from "./documentContext";

const SAYS: Record<string, string> = {
  reading: "Read the document",
  thinking: "Thought about it",
  searching: "Searched the web",
  fetching: "Read a page",
  writing_notes: "Updated its notes",
  writing_comments: "Wrote comments",
  tool_error: "A web lookup failed",
  done: "Finished",
  cancelled: "Cancelled",
  failed: "Failed",
};

export function ActivityLog({ runs }: { runs: Run[] }) {
  if (runs.length === 0) {
    return <p className="muted small">Nothing yet. Review the document and it will appear here.</p>;
  }
  return (
    <ul className="plain runs">
      {runs.map((run) => (
        <RunRow key={run.id} run={run} />
      ))}
    </ul>
  );
}

function RunRow({ run }: { run: Run }) {
  const [steps, setSteps] = useState<RunStep[] | null>(null);
  const [open, setOpen] = useState(false);

  const toggle = async () => {
    setOpen((v) => !v);
    if (steps === null) {
      try {
        const full = await api.get<Run & { steps: RunStep[] }>(`/api/runs/${run.id}`);
        setSteps(full.steps);
      } catch {
        setSteps([]);
      }
    }
  };

  const seconds =
    run.ended_at !== null
      ? Math.round((new Date(run.ended_at).getTime() - new Date(run.started_at).getTime()) / 1000)
      : null;

  return (
    <li className="run">
      <button type="button" className="run-head" onClick={toggle}>
        <span className="run-kind">{run.kind}</span>
        <span className="muted small">
          {when(run.started_at)}
          {seconds !== null ? ` · ${seconds}s` : " · running"}
          {run.status !== "done" ? ` · ${run.status}` : ""}
        </span>
        <span className="num">${run.cost_usd.toFixed(3)}</span>
      </button>

      {open && (
        <div className="run-detail">
          {run.error && (
            <p className="error small">
              {run.error.message}
              {run.error.retryable ? " You can try again." : ""}
            </p>
          )}
          <table className="usage">
            <tbody>
              <tr>
                <td>prompt</td>
                <td className="num">{run.total_prompt_tokens.toLocaleString()}</td>
                <td className="muted small">
                  {run.input_tokens.toLocaleString()} new ·{" "}
                  {run.cache_read_tokens.toLocaleString()} cached ·{" "}
                  {run.cache_write_tokens.toLocaleString()} written
                </td>
              </tr>
              <tr>
                <td>reply</td>
                <td className="num">{run.output_tokens.toLocaleString()}</td>
                <td className="muted small">{run.model}</td>
              </tr>
              {(run.search_count > 0 || run.fetch_count > 0) && (
                <tr>
                  <td>web</td>
                  <td className="num">{run.search_count + run.fetch_count}</td>
                  <td className="muted small">
                    {run.search_count} search{run.search_count === 1 ? "" : "es"} ·{" "}
                    {run.fetch_count} page{run.fetch_count === 1 ? "" : "s"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {steps === null && <p className="muted small">Loading…</p>}
          {steps !== null && steps.length > 0 && (
            <ol className="steps">
              {steps.map((step, i) => (
                <li key={i}>
                  <span className="muted small">{SAYS[step.type] ?? step.type}</span>
                  {step.detail && <span> — {step.detail}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}

/** The log as a route, one step inside Research (design §14.4). */
export function ActivityLogRoute() {
  const { advisor } = useDocument();
  return <ActivityLog runs={advisor.runs} />;
}
